// KpiHeatmap — KPI rows against calendar months, one cell per KPI-month.
//
// It is a real table, not a grid of divs, because that is what it is: every
// cell is identified by its KPI and its month, and a screen reader announcing
// those two headers is the whole reason the shape works without sight.
//
// Colour is never the only signal. Each cell carries a sentence naming the
// month, the measured value, the target and the outcome, and that sentence is
// the cell's accessible name — present in the DOM whether or not anyone
// hovers. The visible card is a convenience for mouse and keyboard users and
// is hidden from assistive tech, so nothing is announced twice.
//
// A cell with no results is not interactive. A tab stop that leads to "no
// measurement due" wastes a keyboard user's time, and on a twelve-month view
// of a quarterly KPI there are eight of them per row.
//
// The measured cells share ONE tab stop and are walked with the arrow keys,
// the roving-tabindex pattern. Sixty cells each holding their own stop would
// bury every control after the grid. Arrows step through the measured cells in
// row-major order and stop at the ends rather than wrapping, so a reader can
// tell by feel that they have reached the edge of the data.
//
// The scroll wrapper is `relative` deliberately: the visually-hidden
// sentences inside it are absolutely positioned, and without a positioned
// ancestor they resolve against the page and widen the document — a 400px
// viewport gained 268px of horizontal scroll before this was set.

import { useRef, useState, type JSX, type KeyboardEvent } from "react";
import { clsx } from "clsx";
import { CELL_STATE_LABEL, describeKpiCell } from "./kpiCellText";
import { formatReportMonth, formatReportMonthShort } from "./reportFormat";
import type {
  KpiCellState,
  KpiHeatmapCell,
  KpiHeatmapRow,
  ReportMonth,
} from "../../../types/report";

/** Cell tones. `notClosed` is deliberately an outlined blank rather than a
 *  filled slate: a period that closed with nobody reporting is an absence
 *  someone owes, and it must not read like a month where nothing was due.
 *  The outline is slate-400/500 so it clears the 3:1 non-text contrast floor
 *  against the card — a lighter hairline made the owed report invisible. */
const STATE_CLASS: Record<KpiCellState, string> = {
  met: "bg-emerald-500 dark:bg-emerald-500",
  atRisk: "bg-amber-400 dark:bg-amber-400",
  breached: "bg-red-500 dark:bg-red-500",
  notDue: "bg-slate-200 dark:bg-slate-700",
  notClosed:
    "border border-dashed border-slate-400 bg-transparent dark:border-slate-500",
};

const SWATCH_CLASS = "block h-6 w-full rounded-sm";

const FOCUS_CLASS =
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900";

/** Identifies a cell for the roving tab order. */
function cellKey(kpiId: string, month: ReportMonth): string {
  return `${kpiId}::${month}`;
}

export interface KpiHeatmapProps {
  rows: KpiHeatmapRow[];
  /** The window's months, ascending — the column order. Cells are looked up
   *  by month rather than by position so a row that is short or out of order
   *  cannot silently shift a whole KPI's history sideways. */
  months: ReportMonth[];
}

export default function KpiHeatmap({
  rows,
  months,
}: KpiHeatmapProps): JSX.Element {
  // Row-major order of the cells a reader can actually land on.
  const focusOrder: string[] = [];
  for (const row of rows) {
    for (const month of months) {
      const cell = row.cells.find((c) => c.month === month);
      if (cell && cell.results.length > 0) {
        focusOrder.push(cellKey(row.kpiId, month));
      }
    }
  }

  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const cellRefs = useRef(new Map<string, HTMLButtonElement>());

  // The stop sits on the cell the reader last left, falling back to the first
  // measured one. Reading it from the current order rather than from state
  // means a refetch that changes the grid cannot strand the tab stop on a
  // cell that no longer exists.
  const tabStopKey =
    focusedKey !== null && focusOrder.includes(focusedKey)
      ? focusedKey
      : (focusOrder[0] ?? null);

  function moveFocus(from: string, delta: number) {
    const index = focusOrder.indexOf(from);
    const next = index + delta;
    if (index === -1 || next < 0 || next >= focusOrder.length) return;
    const key = focusOrder[next];
    setFocusedKey(key);
    cellRefs.current.get(key)?.focus();
  }

  return (
    <div
      role="group"
      aria-label="KPI compliance by month"
      tabIndex={0}
      data-testid="kpi-heatmap"
      className={clsx("relative overflow-x-auto", FOCUS_CLASS)}
    >
      <table className="w-full min-w-[40rem] border-separate border-spacing-x-1 border-spacing-y-1">
        <thead>
          <tr>
            <th
              scope="col"
              className="w-56 px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400"
            >
              KPI
            </th>
            {months.map((month) => (
              <th
                key={month}
                scope="col"
                className="px-1 py-2 text-center text-overline uppercase text-slate-600 dark:text-slate-400"
              >
                <span aria-hidden="true">{formatReportMonthShort(month)}</span>
                <span className="sr-only">{formatReportMonth(month)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.kpiId}>
              <th
                scope="row"
                className="px-3 py-1 text-left text-body-sm font-normal text-slate-900 dark:text-slate-100"
              >
                {row.kpiName}
              </th>
              {months.map((month) => {
                const cell = row.cells.find((c) => c.month === month);
                const key = cellKey(row.kpiId, month);
                return (
                  <td
                    key={month}
                    data-testid={`kpi-heatmap-cell-${row.kpiId}-${month}`}
                    className="px-1 py-1 align-middle"
                  >
                    {cell ? (
                      <HeatmapCell
                        row={row}
                        cell={cell}
                        isTabStop={key === tabStopKey}
                        onMove={(delta) => moveFocus(key, delta)}
                        onFocus={() => setFocusedKey(key)}
                        registerRef={(element) => {
                          if (element) cellRefs.current.set(key, element);
                          else cellRefs.current.delete(key);
                        }}
                      />
                    ) : (
                      <span className={clsx(SWATCH_CLASS, STATE_CLASS.notDue)}>
                        <span className="sr-only">
                          {CELL_STATE_LABEL.notDue}
                        </span>
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface HeatmapCellProps {
  row: KpiHeatmapRow;
  cell: KpiHeatmapCell;
  isTabStop: boolean;
  /** −1 for the previous measured cell, +1 for the next. */
  onMove: (delta: number) => void;
  onFocus: () => void;
  registerRef: (element: HTMLButtonElement | null) => void;
}

function HeatmapCell({
  row,
  cell,
  isTabStop,
  onMove,
  onFocus,
  registerRef,
}: HeatmapCellProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const description = describeKpiCell(row.kpiName, row.target, cell);

  // A cell with nothing measured has nothing to disclose, so it stays inert
  // and out of the tab order — the state word is already in the table.
  if (cell.results.length === 0) {
    return (
      <span
        className={clsx(SWATCH_CLASS, STATE_CLASS[cell.state])}
        data-state={cell.state}
      >
        <span className="sr-only">{description}</span>
      </span>
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape") {
      // Stops the key reaching an enclosing dialog, which would close the
      // whole report rather than the card the reader was looking at.
      event.stopPropagation();
      setOpen(false);
      return;
    }
    // Down and Right both step forward through the measured cells. A column
    // walk is not offered because a column is mostly gaps: on a quarterly KPI
    // the cell below is eight months away and reads as a jump, not a step.
    const delta =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    onMove(delta);
  }

  return (
    <span className="relative block">
      <button
        ref={registerRef}
        type="button"
        data-state={cell.state}
        tabIndex={isTabStop ? 0 : -1}
        className={clsx(SWATCH_CLASS, STATE_CLASS[cell.state], FOCUS_CLASS)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          onFocus();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
      >
        <span className="sr-only">{description}</span>
      </button>
      {open && (
        <span
          aria-hidden="true"
          data-testid={`kpi-heatmap-card-${row.kpiId}-${cell.month}`}
          className="absolute bottom-full left-1/2 z-10 mb-1 block w-max max-w-xs -translate-x-1/2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-caption text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        >
          {description}
        </span>
      )}
    </span>
  );
}
